import { useEffect, useRef, useState } from "react";
import SectionLabel from "./SectionLabel";

type Stat = {
  value: string;
  numeric: number;
  label: string;
  size: number;
  bg: string;
  color: string;
  ring: string;
  /** Rotation degrees applied during growth. */
  spin: number;
  /** Idle continuous breathing amplitude (px). */
  idleAmp: number;
  /** Idle float duration (s). */
  idleDur: number;
  /** Idle float delay (s). */
  idleDelay: number;
  /** Mouse parallax strength multiplier. */
  parallax: number;
};

const STATS: Stat[] = [
  {
    value: "43",
    numeric: 43,
    label: "Billion (NOK) in Total Assets",
    size: 400,
    bg: "#93C5FD",
    color: "#0B0B0B",
    ring: "rgba(147,197,253,0.5)",
    spin: -18,
    idleAmp: 10,
    idleDur: 7.5,
    idleDelay: 0,
    parallax: 1,
  },
  {
    value: "250",
    numeric: 250,
    label: "Hotels across the Nordics",
    size: 260,
    bg: "#60A5FA",
    color: "#0B1B4D",
    ring: "rgba(96,165,250,0.5)",
    spin: 22,
    idleAmp: 14,
    idleDur: 6.2,
    idleDelay: 1.1,
    parallax: 1.6,
  },
  {
    value: "21",
    numeric: 21,
    label: "Billion (NOK) in Gross Real Estate Value",
    size: 220,
    bg: "#60A5FA",
    color: "#0B1B4D",
    ring: "rgba(96,165,250,0.5)",
    spin: -14,
    idleAmp: 12,
    idleDur: 8.4,
    idleDelay: 0.6,
    parallax: 1.8,
  },
  {
    value: "30 000",
    numeric: 30000,
    label: "Employees in total — Strawberry Hotels, NLTG & Stureplansgruppen",
    size: 460,
    bg: "#2563EB",
    color: "#ECECEC",
    ring: "rgba(37,99,235,0.55)",
    spin: 20,
    idleAmp: 8,
    idleDur: 9.1,
    idleDelay: 1.8,
    parallax: 0.8,
  },
];

const POSITIONS: React.CSSProperties[] = [
  { left: "-2%", top: 220 },
  { left: "30%", top: 80 },
  { left: "44%", top: 340 },
  { right: "-6%", top: 130 },
];

function useSectionProgress(ref: React.RefObject<HTMLElement | null>) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let rafId = 0;

    const update = () => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const total = rect.height + vh;
      const passed = vh - rect.top;
      const p = Math.min(1, Math.max(0, passed / total));
      setProgress(p);
    };

    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [ref]);

  return progress;
}

const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
const easeOutBack = (t: number) => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};

export default function NumbersSection() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const progress = useSectionProgress(sectionRef);
  const [mouse, setMouse] = useState({ x: 0.5, y: 0.5 });

  // Bubbles complete their growth within the first ~55% of the section
  const enter = easeOut(Math.min(1, progress / 0.55));

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = stageRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const mx = (e.clientX - rect.left) / rect.width;
    const my = (e.clientY - rect.top) / rect.height;
    setMouse({ x: mx, y: my });
  };

  const onMouseLeave = () => setMouse({ x: 0.5, y: 0.5 });

  const parallaxX = (mouse.x - 0.5) * 40;
  const parallaxY = (mouse.y - 0.5) * 40;

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden"
      style={{
        background: "#000",
        padding: "80px 40px 160px",
        fontFamily: "'Space Grotesk', system-ui, sans-serif",
      }}
    >
      {/* Faint moving grid backdrop */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
          transform: `translate3d(${-parallaxX * 0.4}px, ${
            -parallaxY * 0.4
          }px, 0)`,
          transition: "transform 0.5s ease-out",
          maskImage:
            "radial-gradient(ellipse at center, rgba(0,0,0,1) 20%, rgba(0,0,0,0) 75%)",
        }}
      />

      {/* Mouse-follow soft spotlight */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(600px circle at ${mouse.x * 100}% ${
            mouse.y * 100
          }%, rgba(147,197,253,0.08), transparent 60%)`,
          transition: "background 0.2s ease-out",
        }}
      />

      <div className="relative mx-auto" style={{ maxWidth: 1440 }}>
        <SectionLabel>Strawberry in Numbers</SectionLabel>

        <div
          ref={stageRef}
          className="relative mt-16"
          style={{ height: 720, width: "100%" }}
          onMouseMove={onMouseMove}
          onMouseLeave={onMouseLeave}
        >
          {/* SVG connection lines drawn between circles once they're in */}
          <ConnectionLines enter={enter} />

          {/* Ambient tiny circles — scale in with parallax */}
          <AmbientDot
            style={{
              width: 22,
              height: 22,
              background: "#93C5FD",
              left: "36%",
              top: 90,
            }}
            enter={enter}
            idleAmp={6}
            idleDur={5.4}
            idleDelay={0.3}
            parallax={{ x: parallaxX * 1.4, y: parallaxY * 1.4 }}
          />
          <AmbientDot
            style={{
              width: 34,
              height: 34,
              background: "#0B1B4D",
              border: "1px solid #1E3A8A",
              right: "18%",
              bottom: 60,
            }}
            enter={enter}
            idleAmp={9}
            idleDur={6.6}
            idleDelay={1.4}
            parallax={{ x: parallaxX * -1.6, y: parallaxY * -1.6 }}
          />
          <AmbientDot
            style={{
              width: 16,
              height: 16,
              background: "#8A8A8A",
              left: "60%",
              top: 20,
            }}
            enter={enter}
            idleAmp={5}
            idleDur={4.8}
            idleDelay={0.8}
            parallax={{ x: parallaxX * 2.2, y: parallaxY * 2.2 }}
          />
          <AmbientDot
            style={{
              width: 26,
              height: 26,
              background: "#93C5FD",
              left: "8%",
              bottom: 20,
            }}
            enter={enter}
            idleAmp={7}
            idleDur={7.2}
            idleDelay={2}
            parallax={{ x: parallaxX * -1.2, y: parallaxY * -1.2 }}
          />

          {/* Orbiting micro dots */}
          <OrbitingDots enter={enter} />

          {STATS.map((stat, i) => (
            <StatCircle
              key={stat.value}
              stat={stat}
              style={POSITIONS[i]}
              enter={enter}
              delay={i * 0.14}
              index={i}
              parallax={{
                x: parallaxX * stat.parallax,
                y: parallaxY * stat.parallax,
              }}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes sg-idle-float {
          0%, 100% { transform: translate3d(0, 0, 0); }
          50%      { transform: translate3d(0, calc(var(--sg-idle-amp, 10) * -1px), 0); }
        }
        @keyframes sg-pulse-ring {
          0%   { transform: scale(1);   opacity: 0.55; }
          80%  { transform: scale(1.6); opacity: 0;    }
          100% { transform: scale(1.6); opacity: 0;    }
        }
        @keyframes sg-shine-sweep {
          0%   { transform: translateX(-140%) skewX(-20deg); }
          100% { transform: translateX(180%)  skewX(-20deg); }
        }
        @keyframes sg-orbit-a {
          from { transform: rotate(0deg)   translateX(160px) rotate(0deg);   }
          to   { transform: rotate(360deg) translateX(160px) rotate(-360deg); }
        }
        @keyframes sg-orbit-b {
          from { transform: rotate(0deg)   translateX(240px) rotate(0deg);   }
          to   { transform: rotate(-360deg) translateX(240px) rotate(360deg); }
        }
        @keyframes sg-orbit-c {
          from { transform: rotate(0deg)   translateX(320px) rotate(0deg);   }
          to   { transform: rotate(360deg) translateX(320px) rotate(-360deg); }
        }
        @keyframes sg-blink {
          0%, 100% { opacity: 0.35; }
          50%      { opacity: 1;    }
        }
        @keyframes sg-dash {
          to { stroke-dashoffset: -40; }
        }
      `}</style>
    </section>
  );
}

/* ----------------------------------------------------------------------- */

function ConnectionLines({ enter }: { enter: number }) {
  const visible = Math.min(1, Math.max(0, (enter - 0.4) / 0.6));
  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ opacity: visible * 0.35 }}
    >
      <line
        x1="10%"
        y1="52%"
        x2="42%"
        y2="22%"
        stroke="#93C5FD"
        strokeWidth={1}
        strokeDasharray="4 6"
        style={{ animation: "sg-dash 4s linear infinite" }}
      />
      <line
        x1="42%"
        y1="22%"
        x2="56%"
        y2="62%"
        stroke="#60A5FA"
        strokeWidth={1}
        strokeDasharray="4 6"
        style={{ animation: "sg-dash 6s linear infinite" }}
      />
      <line
        x1="56%"
        y1="62%"
        x2="88%"
        y2="36%"
        stroke="#2563EB"
        strokeWidth={1}
        strokeDasharray="4 6"
        style={{ animation: "sg-dash 5s linear infinite" }}
      />
      <line
        x1="10%"
        y1="52%"
        x2="88%"
        y2="36%"
        stroke="#93C5FD"
        strokeWidth={1}
        strokeDasharray="2 10"
        style={{ animation: "sg-dash 8s linear infinite" }}
      />
    </svg>
  );
}

function OrbitingDots({ enter }: { enter: number }) {
  if (enter < 0.9) return null;
  return (
    <div
      className="absolute pointer-events-none"
      style={{
        left: "22%",
        top: "38%",
        width: 0,
        height: 0,
      }}
    >
      <span
        className="absolute block rounded-full"
        style={{
          width: 8,
          height: 8,
          background: "#93C5FD",
          animation: "sg-orbit-a 14s linear infinite",
        }}
      />
      <span
        className="absolute block rounded-full"
        style={{
          width: 6,
          height: 6,
          background: "#60A5FA",
          animation: "sg-orbit-b 22s linear infinite",
        }}
      />
      <span
        className="absolute block rounded-full"
        style={{
          width: 5,
          height: 5,
          background: "#2563EB",
          animation: "sg-orbit-c 30s linear infinite",
        }}
      />
    </div>
  );
}

/* ----------------------------------------------------------------------- */

function AmbientDot({
  style,
  enter,
  idleAmp,
  idleDur,
  idleDelay,
  parallax,
}: {
  style: React.CSSProperties;
  enter: number;
  idleAmp: number;
  idleDur: number;
  idleDelay: number;
  parallax: { x: number; y: number };
}) {
  const scale = enter;
  return (
    <span
      className="absolute rounded-full"
      style={{
        ...style,
        transform: `translate3d(${parallax.x}px, ${parallax.y}px, 0) scale(${scale})`,
        opacity: enter,
        transition: "transform 0.4s ease-out",
      }}
    >
      <span
        aria-hidden
        style={
          {
            display: "block",
            width: "100%",
            height: "100%",
            borderRadius: "9999px",
            background: "inherit",
            border: "inherit",
            "--sg-idle-amp": idleAmp,
            animation:
              enter > 0.9
                ? `sg-idle-float ${idleDur}s ease-in-out ${idleDelay}s infinite, sg-blink ${
                    idleDur * 0.7
                  }s ease-in-out ${idleDelay}s infinite`
                : "none",
          } as React.CSSProperties
        }
      />
    </span>
  );
}

/* ----------------------------------------------------------------------- */

function StatCircle({
  stat,
  style,
  enter,
  delay,
  index,
  parallax,
}: {
  stat: Stat;
  style: React.CSSProperties;
  enter: number;
  delay: number;
  index: number;
  parallax: { x: number; y: number };
}) {
  const [hovered, setHovered] = useState(false);
  const [burstKey, setBurstKey] = useState(0);

  const raw = (enter - delay) / (1 - delay);
  const t = Math.min(1, Math.max(0, raw));
  const eScale = easeOutBack(t); // grows from tiny to full with a soft overshoot
  const e = easeOut(t); // for opacity / text

  // Grow from a tiny dot (~4%) to full size.
  const growth = 0.04 + eScale * 0.96;
  const hoverScale = hovered ? 1.06 : 1;
  const scale = growth * hoverScale;
  const rotation = stat.spin * (1 - e);
  const opacity = e;

  const displayNum = Math.round(stat.numeric * e);
  const display = formatNumber(displayNum, stat.value);

  const showText = e > 0.55;

  return (
    <div
      className="absolute cursor-pointer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => setBurstKey((k) => k + 1)}
      style={{
        width: stat.size,
        height: stat.size,
        transform: `translate3d(${parallax.x}px, ${parallax.y}px, 0)`,
        transition: "transform 0.5s cubic-bezier(0.2, 0.8, 0.2, 1)",
        willChange: "transform",
        ...style,
      }}
    >
      {/* Pulse rings — only after growth completes */}
      {e > 0.95 && (
        <>
          <span
            aria-hidden
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              border: `2px solid ${stat.ring}`,
              animation: `sg-pulse-ring ${4 + index * 0.4}s ease-out ${
                index * 0.5
              }s infinite`,
            }}
          />
          <span
            aria-hidden
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              border: `1px solid ${stat.ring}`,
              animation: `sg-pulse-ring ${5 + index * 0.3}s ease-out ${
                index * 0.5 + 1.4
              }s infinite`,
            }}
          />
        </>
      )}

      {/* Click burst ring */}
      {burstKey > 0 && (
        <span
          key={burstKey}
          aria-hidden
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            border: `2px solid ${stat.ring}`,
            animation: "sg-pulse-ring 0.9s ease-out forwards",
          }}
        />
      )}

      {/* Main bubble */}
      <div
        className="relative w-full h-full rounded-full flex flex-col items-center justify-center text-center overflow-hidden"
        style={{
          background: stat.bg,
          color: stat.color,
          padding: 32,
          transform: `scale(${scale}) rotate(${rotation}deg)`,
          opacity,
          transition:
            "transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.35s ease-out",
          willChange: "transform",
          boxShadow: hovered
            ? `0 0 0 6px ${stat.ring}, 0 30px 80px -20px ${stat.ring}`
            : `0 0 0 0 ${stat.ring}, 0 0 0 -20px ${stat.ring}`,
        }}
      >
        {/* Idle float wrapper */}
        <div
          className="relative flex flex-col items-center justify-center w-full h-full"
          style={
            {
              "--sg-idle-amp": stat.idleAmp,
              animation:
                e > 0.95
                  ? `sg-idle-float ${stat.idleDur}s ease-in-out ${
                      stat.idleDelay + index * 0.3
                    }s infinite`
                  : "none",
            } as React.CSSProperties
          }
        >
          <div
            style={{
              fontSize: Math.max(48, stat.size * 0.22),
              fontWeight: 700,
              letterSpacing: "-0.03em",
              lineHeight: 1,
              opacity: showText ? 1 : 0,
              transform: showText ? "translateY(0)" : "translateY(8px)",
              transition: "opacity 0.4s ease-out, transform 0.4s ease-out",
            }}
          >
            {display}
          </div>
          <div
            style={{
              marginTop: 12,
              fontSize: 13,
              fontWeight: 500,
              opacity: showText ? 0.85 : 0,
              maxWidth: stat.size * 0.68,
              lineHeight: 1.3,
              transform: showText ? "translateY(0)" : "translateY(6px)",
              transition:
                "opacity 0.5s ease-out 0.1s, transform 0.5s ease-out 0.1s",
            }}
          >
            {stat.label}
          </div>
        </div>

        {/* Shine sweep on hover */}
        {hovered && (
          <span
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              overflow: "hidden",
              borderRadius: "9999px",
            }}
          >
            <span
              className="absolute top-0 left-0 h-full"
              style={{
                width: "40%",
                background:
                  "linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)",
                animation: "sg-shine-sweep 1.1s ease-out forwards",
              }}
            />
          </span>
        )}
      </div>
    </div>
  );
}

function formatNumber(n: number, original: string): string {
  if (original.includes(" ")) {
    return n.toLocaleString("en-US").replace(/,/g, " ");
  }
  return String(n);
}